import {CacheDataService} from '../../../api/services/cache-data.service';
import {ElementRef, Input, NgZone} from '@angular/core';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {filter, map, pairwise, throttleTime} from 'rxjs/operators';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {isEqual} from 'lodash';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {MatLegacyChipInputEvent} from '@angular/material/legacy-chips';

export class TextSearchValue<TSourceType> {
  searchTerms: string[] = [];
  selectedValues: TSourceType[] = [];
}

export abstract class TextSearchFilterComponent<TSourceType>
{
  protected readonly separatorKeysCodes = [ENTER, COMMA] as const;

  currentPage: number = -1;
  totalPages: number = -1;

  selectedValuesPage: number = -1;
  selectedValuesTotalPages: number = -1;
  paginatedSelectedValues: TSourceType[] = [];

  sourceValues: TSourceType[] = [];

  reloading: boolean = false;
  loading: boolean = false;
  loadingSelected: boolean = false;

  protected searchCounter: number = 0;
  protected selectedKeys = new Set<string>();

  protected _value: TextSearchValue<TSourceType> = {
    searchTerms: [],
    selectedValues: []
  };
  private _onChangedCallback: (value: TSourceType[]) => void = () => {};
  private _onTouchedCallback: () => void = () => {};

  protected constructor(
    protected cacheDataService: CacheDataService,
    protected ngZone: NgZone
    ) {
  }

  public getHeight() : number {
    const height = this.getHeightValue();
    if (height) {
      return height;
    }

    return 0;
  }

  protected callNgOnInit() {
    this.newSearch(false, false);
  }

  public forceRefresh() {
    this.newSearch(true, true);
  }

  protected callNgAfterViewInit() {
    /*this.getCdkScroller().elementScrolled().pipe(
      map(() => this.getCdkScroller().measureScrollOffset('bottom')),
      pairwise(),
      filter(([y1, y2]) => (y2 < y1 && y2 < 80)),
      throttleTime(200)
    ).subscribe(() => {
        this.ngZone.run(() => {
          this.getNextBatch();
        });
      }
    );*/
  }

  protected abstract getIdentifier(item: TSourceType): string;

  protected abstract newSearch(hasSearchChanges: boolean, hasSelectedChanges: boolean);
  protected abstract getNextBatch();
  protected abstract getNextSelectedBatch();
  protected abstract getSearchPlaceholder(): string;

  protected abstract isSelected(target: TSourceType) : boolean;
  protected abstract getDisplay(target: TSourceType): string;
  protected abstract getDivScroller(): ElementRef;

  protected abstract getShowSelected(): boolean;

  protected abstract dataUpdated() : void;

  protected abstract getHeightValue() : number | undefined;

  onScroll(event: Event) {
    if (this.reloading) return;
    const element = event.target as HTMLElement;
    if (element) {
      if (element.scrollHeight > element.clientHeight && element.scrollTop + element.clientHeight >= element.scrollHeight - 50) {
        if (!this.loadingSelected && this.selectedValuesPage < this.selectedValuesTotalPages) {
          this.getNextSelectedBatch();
        } else if (!this.loading && this.currentPage < this.totalPages) {
          this.getNextBatch();
        }
      }
    }
  }

  onAddSearch($event: MatLegacyChipInputEvent) {
    const newSearch = $event.value;
    if (newSearch.trim() !== '') {
      const existing = this._value?.searchTerms?.filter(x => x.toLowerCase().trim() === newSearch.toLowerCase().trim());
      if (existing && existing.length > 0) return;
      this.value = {
        searchTerms: [...this._value?.searchTerms ?? [], newSearch],
        selectedValues: this._value.selectedValues
      }

      $event.chipInput.clear();

      this.getDivScroller().nativeElement.scrollTop = 0;
    }
  }

  onRemoveSearch(search: string) {
    const newSearchTerms = [...this._value.searchTerms];
    newSearchTerms.splice(newSearchTerms.indexOf(search), 1);
    this.value = {
      searchTerms: newSearchTerms,
      selectedValues: this._value.selectedValues
    }
    this.getDivScroller().nativeElement.scrollTop = 0;
  }

  checkChanged($event: MatCheckboxChange, sourceValue: TSourceType) {
    if ($event.checked) {
      this.value = {
        searchTerms: this._value?.searchTerms ?? [],
        selectedValues: [...this._value?.selectedValues ?? [], sourceValue]
      };
    } else {
      const newSelectedValues = [...this._value?.selectedValues ?? []];
      newSelectedValues.splice(this._value.selectedValues.indexOf(sourceValue), 1);
      this.value = {
        searchTerms: this._value.searchTerms,
        selectedValues: newSelectedValues
      };
    }
  }

  get value(): any {
    return this._value;
  }

  set value(v: any) {
    if (v !== this._value) {
      let hasSearchChanges = false;
      let hasSelectedChanges = false;

      const origList = this._value?.searchTerms ?? [];
      const newList = v?.searchTerms ?? [];

      const origSelected = this._value?.selectedValues ?? [];
      const newSelected = v?.selectedValues ?? [];

      hasSearchChanges = !isEqual(origList, newList);
      hasSelectedChanges = !isEqual(origSelected, newSelected);

      const isFirstLoad = this.currentPage === -1;

      this._value = v;

      if (hasSelectedChanges || isFirstLoad) {
        this.selectedKeys.clear();
        (this._value?.selectedValues ?? []).forEach(x => {
          this.selectedKeys.add(this.getIdentifier(x));
        });
      }

      this._onChangedCallback(v);

      this.dataUpdated();

      if (hasSearchChanges || hasSelectedChanges || isFirstLoad) {
        this.newSearch(hasSearchChanges, hasSelectedChanges);
      }
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this._onChangedCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Implement if needed
  }
}
